import type { FriendlyError } from '@/lib/errors/types';
import { getTestError } from '@/lib/errors/testErrors';
import { captureFriendlyError } from '@/lib/sentry';
import api from './client';
import type { ApiError } from './client';

// ---------------------------------------------------------
// Types
// ---------------------------------------------------------

export interface TestQuestion {
  id: number;
  question_text: string;
  category: string;
  explanation: string | null;
  hint: string | null;
}

export interface StartTestResponse {
  test_attempt_id: string;
  started_at: string;
}

export interface TestQuestionsResponse {
  test_attempt_id: string;
  questions: TestQuestion[];
  total_questions: number;
}

export interface TestAnswer {
  question_id: number;
  user_answer: string;
}

export interface CompleteTestResponse {
  test_attempt_id: string;
  status: 'completed';
  total_questions: number;
  correct_answers: number;
  score_percentage: number;
  passed: boolean;
  completed_at: string;
}

export interface WeakCategory {
  category: string;
  total: number;
  correct: number;
  percentage: number;
}

export interface TestResultsResponse {
  test_attempt: {
    id: string;
    status: string;
    started_at: string;
    completed_at: string | null;
    score: number | null;
    total_questions: number;
    correct_answers: number | null;
  };
  summary: {
    total_questions: number;
    correct_answers: number;
    incorrect_answers: number;
    score_percentage: number;
    passed: boolean;
    time_taken_minutes: number;
    weak_categories: WeakCategory[];
  };
}

export interface QuestionAttempt {
  id: string;
  question_id: number;
  user_answer: string;
  is_correct: boolean;
  question: {
    id: number;
    category: string;
    question_text: string;
    answer_config: {
      type: string;
      accepted_answers: string[];
    };
    explanation: string | null;
  };
}

export interface TestReviewResponse {
  question_attempts: QuestionAttempt[];
}

// ---------------------------------------------------------
// Result types with error handling
// ---------------------------------------------------------

export type StartTestResult =
  | { success: true; data: StartTestResponse }
  | { success: false; error: FriendlyError };

export type GetQuestionsResult =
  | { success: true; data: TestQuestionsResponse }
  | { success: false; error: FriendlyError };

export type CompleteTestResult =
  | { success: true; data: CompleteTestResponse }
  | { success: false; error: FriendlyError };

export type GetResultsResult =
  | { success: true; data: TestResultsResponse }
  | { success: false; error: FriendlyError };

export type GetReviewResult =
  | { success: true; data: TestReviewResponse }
  | { success: false; error: FriendlyError };

// ---------------------------------------------------------
// API Functions
// ---------------------------------------------------------

/**
 * Start a new mock interview test
 * Creates a test attempt with random civics questions
 */
export async function startTest(): Promise<StartTestResult> {
  try {
    const response = await api.post<{ success: boolean; data: StartTestResponse }>(
      '/api/test/start'
    );
    return { success: true, data: response.data };
  } catch (error) {
    const apiError = error as ApiError;
    const friendly = getTestError(apiError.code || 'start_test_failed');
    captureFriendlyError(error, friendly);
    return { success: false, error: friendly };
  }
}

/**
 * Get questions for a test attempt
 * @param testAttemptId - UUID of the test attempt
 */
export async function getTestQuestions(
  testAttemptId: string
): Promise<GetQuestionsResult> {
  try {
    const response = await api.get<{ success: boolean; data: TestQuestionsResponse }>(
      `/api/test/${testAttemptId}/questions`
    );
    return { success: true, data: response.data };
  } catch (error) {
    const apiError = error as ApiError;
    const friendly = getTestError(apiError.code || 'questions_load_failed');
    captureFriendlyError(error, friendly);
    return { success: false, error: friendly };
  }
}

/**
 * Complete a test by submitting all answers
 * Triggers AI grading and returns the score
 * @param testAttemptId - UUID of the test attempt
 * @param answers - Array of question answers
 */
export async function completeTest(
  testAttemptId: string,
  answers: TestAnswer[]
): Promise<CompleteTestResult> {
  try {
    const response = await api.post<{ success: boolean; data: CompleteTestResponse }>(
      `/api/test/${testAttemptId}/complete`,
      { answers }
    );
    return { success: true, data: response.data };
  } catch (error) {
    const apiError = error as ApiError;
    const friendly = getTestError(apiError.code || 'test_submit_failed');
    captureFriendlyError(error, friendly);
    return { success: false, error: friendly };
  }
}

/**
 * Get test results summary
 * Includes score, pass/fail status, time taken, and weak categories
 * @param testAttemptId - UUID of the test attempt
 */
export async function getTestResults(
  testAttemptId: string
): Promise<GetResultsResult> {
  try {
    const response = await api.get<{ success: boolean; data: TestResultsResponse }>(
      `/api/test/${testAttemptId}/results`
    );
    return { success: true, data: response.data };
  } catch (error) {
    const apiError = error as ApiError;
    const friendly = getTestError(apiError.code || 'results_load_failed');
    captureFriendlyError(error, friendly);
    return { success: false, error: friendly };
  }
}

/**
 * Get detailed test review
 * Shows each question with user's answer and correct answer
 * @param testAttemptId - UUID of the test attempt
 */
export async function getTestReview(
  testAttemptId: string
): Promise<GetReviewResult> {
  try {
    const response = await api.get<{ success: boolean; data: TestReviewResponse }>(
      `/api/test/${testAttemptId}/review`
    );
    return { success: true, data: response.data };
  } catch (error) {
    const apiError = error as ApiError;
    const friendly = getTestError(apiError.code || 'review_load_failed');
    captureFriendlyError(error, friendly);
    return { success: false, error: friendly };
  }
}
